// Tech Inspector agent — precise, technical Q&A persona. Slash commands
// run locally against resumeData; free-text falls through to Gemini using
// `systemPrompt` below as grounded context.

import type {
  AgentConfig,
  SlashCommand,
  SlashCommandResult,
} from '../AgentConfig';
import { resumeData } from '../../../../data/resumeData';

// ---------------------------------------------------------------------------
// Skill knowledge base
// ---------------------------------------------------------------------------
//
// Years are inferred from Thanh's career timeline:
//   - Testing career began Aug 2020 at TMA Solutions (5+ years by 2026).
//   - Python used from day 1 of his testing career.
//   - Selenium + Robot Framework + Java started at TMA (~4 yrs).
//   - Postman + Jenkins picked up at TMA (CCaaS LE project) and deepened
//     at Terralogic.
//   - JMeter became a core tool at Terralogic Project Leader (rate-limit
//     testing) — roughly 2+ years.
//   - Playwright is the newest framework — adopted more recently
//     (~1+ year, post-MODEC onboarding).
//
// Shape: name → { years, scale (1-12 for bar), category, where, projects[] }
interface TechFact {
  years: string;
  /** Bar magnitude 1-12 for the ASCII chart. */
  scale: number;
  category: string;
  where: string[];
  projects: string[];
  note?: string;
}

const TECH_FACTS: Record<string, TechFact> = {
  python: {
    years: '5+ yrs',
    scale: 11,
    category: 'primary language',
    where: ['MODEC', 'Terralogic', 'TMA Solutions'],
    projects: [
      'External API automation for Cisco DNAC (Terralogic, 2022-2023)',
      'CCaaS LE installation automation with Python + Postman + Jenkins (TMA)',
    ],
  },
  selenium: {
    years: '4 yrs',
    scale: 8,
    category: 'web UI',
    where: ['TMA Solutions', 'Terralogic'],
    projects: [
      'Web Portal automation for Avaya Ready Now Solution (TMA, Project 2)',
    ],
  },
  playwright: {
    years: '1+ yr',
    scale: 3,
    category: 'web UI (newer)',
    where: ['MODEC'],
    projects: [
      'Internal AI Chat regression automation (MODEC, 2026)',
    ],
    note: 'Newest framework in his toolkit — adopted as part of the MODEC stack.',
  },
  robot: {
    years: '3+ yrs',
    scale: 7,
    category: 'acceptance / keyword-driven',
    where: ['TMA Solutions'],
    projects: [
      'Avaya Ready Now Solution web portal acceptance suite (Selenium + Robot + Python)',
    ],
  },
  'robot-framework': {
    years: '3+ yrs',
    scale: 7,
    category: 'acceptance / keyword-driven',
    where: ['TMA Solutions'],
    projects: [
      'Avaya Ready Now Solution web portal acceptance suite (Selenium + Robot + Python)',
    ],
  },
  jmeter: {
    years: '2+ yrs',
    scale: 5,
    category: 'performance',
    where: ['Terralogic'],
    projects: [
      'Automated rate-limit testing for Catalyst Center APIs (JMeter + Jenkins)',
    ],
  },
  jenkins: {
    years: '4+ yrs',
    scale: 8,
    category: 'CI/CD',
    where: ['Terralogic', 'TMA Solutions'],
    projects: [
      'Rate-limit pipelines for Catalyst Center (Terralogic)',
      'CCaaS LE install jobs orchestrated via Jenkins (TMA)',
    ],
  },
  postman: {
    years: '4+ yrs',
    scale: 8,
    category: 'API testing',
    where: ['Terralogic', 'TMA Solutions'],
    projects: [
      'DNAC external API automation (Terralogic)',
      'CCaaS LE installation flows (TMA)',
    ],
  },
  docker: {
    years: '2+ yrs',
    scale: 4,
    category: 'CI/CD support',
    where: ['Terralogic', 'MODEC'],
    projects: [
      'Containerised test runners for API suites (Terralogic)',
    ],
    note: 'Pragmatic user — not a Docker specialist; uses it to package test environments.',
  },
  git: {
    years: '5+ yrs',
    scale: 11,
    category: 'source control',
    where: ['MODEC', 'Terralogic', 'TMA Solutions'],
    projects: ['Every automation repo across his QC career.'],
  },
  api: {
    years: '5+ yrs',
    scale: 11,
    category: 'specialty domain',
    where: ['MODEC', 'Terralogic', 'TMA Solutions'],
    projects: [
      'Catalyst Center: Wireless, L2/L3, SDA Fabric (Terralogic)',
      'External DNAC APIs (Terralogic)',
      'CCaaS LE flows with Postman + Python Requests (TMA)',
    ],
    note: 'His strongest technical specialty — REST/SOAP, rate-limit, integrated solutions.',
  },
};

// ---------------------------------------------------------------------------
// System prompt — embeds the skill matrix verbatim so Gemini stays grounded.
// ---------------------------------------------------------------------------

function buildSkillContext(): string {
  const lines: string[] = [];
  lines.push('SKILL MATRIX (from resume):');
  for (const cat of resumeData.skills) {
    lines.push(`- ${cat.category}: ${cat.items.join(', ')}`);
  }
  lines.push('');
  lines.push('ROUGH YEARS OF EXPERIENCE (inferred from career timeline; QC career began Aug 2020):');
  lines.push('- Python: 5+ yrs (primary language since TMA)');
  lines.push('- Selenium: 4 yrs (web UI; TMA → Terralogic)');
  lines.push('- Robot Framework: 3+ yrs (TMA, Avaya Ready Now Solution)');
  lines.push('- Postman: 4+ yrs (TMA CCaaS LE → Terralogic DNAC)');
  lines.push('- Jenkins: 4+ yrs (TMA → Terralogic)');
  lines.push('- JMeter: 2+ yrs (Terralogic Catalyst Center rate-limit pipelines)');
  lines.push('- Playwright: 1+ yr (newest framework; MODEC AI Chat regression)');
  lines.push('- Docker: 2+ yrs (pragmatic user, not specialist)');
  lines.push('- Git: 5+ yrs (every QC repo)');
  lines.push('- REST/SOAP API testing: 5+ yrs (strongest specialty domain)');
  lines.push('');
  lines.push('KEY PROJECTS BY STACK:');
  lines.push('- Catalyst Center (Wireless, L2/L3, SDA Fabric): Python + JMeter + Jenkins @ Terralogic 2025-2026');
  lines.push('- DNAC external APIs: Python + Postman @ Terralogic 2022-2023');
  lines.push('- Avaya Ready Now web portal: Selenium + Robot Framework + Python @ TMA');
  lines.push('- Avaya J-100 IP Phone devices: Java + PTI telnet @ TMA');
  lines.push('- CCaaS LE installation flows: Python + Postman + Jenkins @ TMA');
  lines.push('- Internal AI Chat app: Python + Playwright + Postman + Jenkins + manual exploratory @ MODEC 2026+');
  return lines.join('\n');
}

const SKILL_CONTEXT = buildSkillContext();

const SYSTEM_PROMPT = `You are "Tech Inspector" — a precise, technical Q&A persona answering depth questions about Thanh Nguyen's engineering toolkit. You serve hiring managers and engineers who want hard facts, not marketing.

${SKILL_CONTEXT}

PERSONA & TONE
- Precise, technical, frank. No marketing language. No "passionate about", no "synergies".
- Comfortable saying "weak in X" or "not in his current toolkit" when accurate.
- Speak about Thanh in the third person ("he", "his", "Thanh").

OUTPUT RULES
- Numbered lists or tight tables when the question is comparative. Plain sentences otherwise.
- ALWAYS cite years and the project/company where a skill was applied.
- If asked about something not in the skill matrix above (e.g., Kubernetes, Rust, Cypress, Go), say so explicitly — do not invent. Offer the closest adjacent skill instead.
- The visitor is in a narrow terminal pane. Keep responses under ~15 lines.
- Off-topic questions: redirect to the technical scope.`;

// ---------------------------------------------------------------------------
// Slash command handlers
// ---------------------------------------------------------------------------

/** Render a single skill row for the ASCII matrix. */
function skillRow(name: string, scale: number, years: string, note: string): string {
  const bar = '█'.repeat(Math.min(12, scale)).padEnd(12, ' ');
  const paddedName = name.padEnd(11, ' ').slice(0, 11);
  const paddedYears = years.padEnd(8, ' ');
  return `  ${paddedName} ${bar} ${paddedYears} ${note}`;
}

function handleSkills(): SlashCommandResult {
  const output: string[] = [];
  output.push('Skill matrix');
  output.push('────────────');
  output.push('Testing frameworks');
  output.push(skillRow('Selenium', TECH_FACTS.selenium.scale, TECH_FACTS.selenium.years, 'web UI'));
  output.push(skillRow('Robot Fr.', TECH_FACTS.robot.scale, TECH_FACTS.robot.years, 'acceptance'));
  output.push(skillRow('Playwright', TECH_FACTS.playwright.scale, TECH_FACTS.playwright.years, 'web (newer)'));
  output.push('Languages');
  output.push(skillRow('Python', TECH_FACTS.python.scale, TECH_FACTS.python.years, 'primary'));
  output.push('API & perf');
  output.push(skillRow('Postman', TECH_FACTS.postman.scale, TECH_FACTS.postman.years, 'REST/SOAP'));
  output.push(skillRow('REST/SOAP', TECH_FACTS.api.scale, TECH_FACTS.api.years, 'specialty'));
  output.push(skillRow('JMeter', TECH_FACTS.jmeter.scale, TECH_FACTS.jmeter.years, 'perf/rate-limit'));
  output.push('CI/CD');
  output.push(skillRow('Jenkins', TECH_FACTS.jenkins.scale, TECH_FACTS.jenkins.years, 'pipelines'));
  output.push(skillRow('Docker', TECH_FACTS.docker.scale, TECH_FACTS.docker.years, 'pragmatic'));
  output.push(skillRow('Git', TECH_FACTS.git.scale, TECH_FACTS.git.years, 'always'));
  output.push('');
  output.push('Methodology: Agile, SDLC, STLC, BDD.');
  output.push('Drill into any tech with: /depth <tech>');
  return { output };
}

function handleDepth(rawArgs: string): SlashCommandResult {
  const token = rawArgs.trim().toLowerCase();
  if (!token) {
    return {
      output: [
        'Usage: /depth <tech>',
        'Known: python, selenium, playwright, robot, jmeter, jenkins,',
        '       postman, docker, git, api',
      ],
    };
  }
  const fact = TECH_FACTS[token];
  if (!fact) {
    return {
      output: [
        `"${token}" — not in his current toolkit.`,
        '',
        'Closest adjacent skills he does have:',
        '  ● Python (primary language)',
        '  ● Selenium / Playwright / Robot Framework (web UI)',
        '  ● Postman + Python Requests (API)',
        '  ● JMeter (performance)',
        '  ● Jenkins (CI/CD)',
        '',
        'Try /skills for the full matrix or /depth <known-tech>.',
      ],
    };
  }
  const output: string[] = [];
  output.push(`${token} — ${fact.years} (${fact.category})`);
  output.push('─'.repeat(Math.min(40, token.length + fact.years.length + fact.category.length + 6)));
  output.push(`Used at: ${fact.where.join(', ')}`);
  output.push('Example projects:');
  for (const p of fact.projects) {
    output.push(`  ● ${p}`);
  }
  if (fact.note) {
    output.push('');
    output.push(`Note: ${fact.note}`);
  }
  return {
    output,
    followUpPrompt: `How deep is Thanh's ${token} experience really?`,
  };
}

function handleStack(): SlashCommandResult {
  return {
    output: [
      'Current preferred stack — MODEC AI Chat (2026+)',
      '───────────────────────────────────────────────',
      'Thanh\'s working stack today is Python as the primary language,',
      'with Playwright and Selenium for web UI automation, Postman + Python',
      'Requests for API coverage, and Jenkins for CI orchestration. He pairs',
      'this with disciplined manual exploratory testing — appropriate given',
      'he is the sole QC on a greenfield AI product and is writing the test',
      'strategy from scratch.',
      '',
      'Adjacent / open to learning:',
      '  ● Kubernetes (currently uses Docker pragmatically)',
      '  ● Modern observability stacks (logs/metrics/traces)',
      '  ● Deeper LLM evaluation frameworks beyond ad-hoc prompt testing',
      '',
      'Use /depth <tech> to drill in.',
    ],
  };
}

const slashCommands: SlashCommand[] = [
  {
    name: '/skills',
    description: 'Full skill matrix with ASCII-bar depth',
    handler: handleSkills,
  },
  {
    name: '/depth',
    description: 'Years + projects for one technology',
    example: '/depth python',
    handler: handleDepth,
  },
  {
    name: '/stack',
    description: 'Current preferred stack at MODEC',
    handler: handleStack,
  },
];

export const techInspector: AgentConfig = {
  id: 'tech',
  label: 'Tech Inspector',
  shortLabel: 'tech',
  emoji: '*',
  accentColor: 'secondary',
  systemPrompt: SYSTEM_PROMPT,
  welcome: [
    '* Tech Inspector — precise technical Q&A',
    'Ask depth questions or run:',
    '  /skills          full skill matrix',
    '  /depth <tech>    years + projects for one tech',
    '  /stack           current preferred stack',
    '  /help            list all commands',
  ],
  slashCommands,
};
